import { Container } from "layouts";
import { Link } from "react-router-dom";
import {
	FiAlertTriangle,
	FiArrowLeft,
	FiArrowRight,
	FiBook,
	FiBookmark,
	FiChevronRight,
	FiClock,
	FiHelpCircle,
	FiLayers,
	FiSettings,
	FiShield,
	FiUserX,
} from "react-icons/fi";
import AuthLink from "components/v2/Auth/AuthLink";
import useAuthStore from "store/authStore";
import { dataThatCannotBeRecovered } from "utils";
import "./accountDeletion.css";

const affectedData = {
	"Saved preferences": { icon: FiSettings, description: "Your personalised settings and choices." },
	"Purchased books": { icon: FiBook, description: "Access to books you’ve purchased." },
	"Subscriptions made": { icon: FiLayers, description: "Your subscription information and access." },
	"History of books read": { icon: FiClock, description: "The record of books you’ve read." },
	"Reading progress": { icon: FiBookmark, description: "Your saved place in your books." },
};

const steps = [
	{
		title: "Open your account settings",
		content: <>Sign in to Akello Library and go to <strong>My account</strong>.</>,
	},
	{
		title: "Choose Delete account",
		content: <>On a computer, find <strong>Delete account</strong> at the bottom of the account navigation. On mobile, open <strong>Account menu</strong>, then choose <strong>Delete account</strong>.</>,
	},
	{
		title: "Review and confirm",
		content: <>Read the confirmation carefully. Choose <strong>Delete permanently</strong> to finish, or <strong>Keep my account</strong> to cancel.</>,
	},
];

const AccountDeletionManual = () => {
	const loggedIn = useAuthStore((state) => state.loggedIn);

	return (
		<Container
			className="account-deletion-page"
			skipLink={<a className="deletion-skip-link" href="#deletion-content">Skip to deletion instructions</a>}
		>
			<main id="deletion-content" className="deletion-main" tabIndex={-1}>
				<div className="deletion-wrap">
					<nav className="deletion-breadcrumb" aria-label="Breadcrumb">
						<Link to="/">Home</Link>
						<FiChevronRight aria-hidden="true" />
						<Link to="/support">Help & support</Link>
						<FiChevronRight aria-hidden="true" />
						<span aria-current="page">Account deletion</span>
					</nav>

					<header className="deletion-heading">
						<span className="deletion-heading-icon"><FiUserX aria-hidden="true" /></span>
						<div>
							<p className="deletion-eyebrow">YOUR ACCOUNT & PRIVACY</p>
							<h1>Deleting your account</h1>
							<p>Understand what this means for your library and how to take the next step.</p>
						</div>
					</header>

					<div className="deletion-columns">
						<div className="deletion-guide">
							<section className="deletion-card" aria-labelledby="deletion-impact-heading">
								<div className="deletion-warning">
									<FiAlertTriangle aria-hidden="true" />
									<div>
										<strong>Account deletion is permanent</strong>
										<p>Once deleted, your account and its information cannot be recovered. Please review what you’ll lose before continuing.</p>
									</div>
								</div>
								<div className="deletion-card-body">
									<h2 id="deletion-impact-heading">What you’ll lose</h2>
									<p>Deleting your account removes access to the following:</p>
									<ul className="deletion-data-list">
										{dataThatCannotBeRecovered.map((label) => {
											const { icon: Icon = FiLayers, description } = affectedData[label] || {};
											return (
												<li key={label}>
													<span className="deletion-data-icon"><Icon aria-hidden="true" /></span>
													<div><h3>{label}</h3>{description && <p>{description}</p>}</div>
												</li>
											);
										})}
									</ul>
								</div>
							</section>

							<section className="deletion-card deletion-instructions" aria-labelledby="deletion-steps-heading">
								<div className="deletion-card-body">
									<p className="deletion-eyebrow">WHEN YOU’RE READY</p>
									<h2 id="deletion-steps-heading">How to delete your account</h2>
									<p>You’ll complete these steps in your account settings.</p>
									<ol className="deletion-steps">
										{steps.map(({ title, content }, index) => (
											<li key={title}>
												<span className="deletion-step-number" aria-hidden="true">{index + 1}</span>
												<div><h3>{title}</h3><p>{content}</p></div>
											</li>
										))}
									</ol>
								</div>
								<div className="deletion-next-step">
									{loggedIn ? (
										<Link to="/my-account" className="deletion-button deletion-button-primary">Go to account settings <FiArrowRight aria-hidden="true" /></Link>
									) : (
										<AuthLink mode="login" className="deletion-button deletion-button-primary">Sign in to continue <FiArrowRight aria-hidden="true" /></AuthLink>
									)}
									<p>{loggedIn ? "Opening your settings does not delete your account. You’ll review a separate confirmation first." : "Sign in to access your account settings. Deletion still requires a separate confirmation."}</p>
								</div>
							</section>
						</div>

						<aside className="deletion-sidebar" aria-label="Help and related links">
							<section className="deletion-help" aria-labelledby="deletion-help-heading">
								<span className="deletion-help-icon"><FiHelpCircle aria-hidden="true" /></span>
								<h2 id="deletion-help-heading">Need a hand first?</h2>
								<p>If you’re having trouble with your account or library, our support team can help.</p>
								<p>Questions about a purchase or subscription? Get in touch before making a permanent change.</p>
								<Link to="/support" className="deletion-button deletion-button-outline">Contact support <FiArrowRight aria-hidden="true" /></Link>
							</section>
							<div className="deletion-privacy-note">
								<FiShield aria-hidden="true" />
								<div><h2>Your privacy matters</h2><p>Learn how Akello Library handles your information.</p><Link to="/privacy-policy">Read our privacy policy <FiArrowRight aria-hidden="true" /></Link></div>
							</div>
							<Link to="/books" className="deletion-back-link"><FiArrowLeft aria-hidden="true" /> Back to the library</Link>
						</aside>
					</div>
				</div>
			</main>
		</Container>
	);
};

export default AccountDeletionManual;
